import { uniq } from 'lodash';
import { useMemo } from 'react';

import { useFetchOnboardingPrincipalNodesQuery } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import type { KGV2NodeForGrouping } from '@/modules/aiSuggestions/kgV2Resolution.utils';

/**
 * Minimal suggestion shape this hook reads: just the entity's `principalNodeIDs`.
 * Both `AiOnboardingModal_AiSuggestionFragment` and `AiSuggestionForReviewCard`
 * satisfy it structurally, so the hook works for both the onboarding modal and
 * the review card.
 */
interface SuggestionWithPrincipalNodeIDs {
  onboardingEntity?: { principalNodeIDs?: string[] | null } | null;
}

export interface UseOnboardingPrincipalNodesReturn {
  /**
   * KG V2 nodes referenced by the onboarding entities' `principalNodeIDs`,
   * keyed by node ID. Used by `resolvePrincipalsFromNodeMap` to resolve each
   * principal directly to its data-model record.
   */
  principalNodesById: Map<string, KGV2NodeForGrouping>;
  /** Whether the principal-node fetch is in flight. */
  loading: boolean;
}

/**
 * Fetches the KG V2 nodes referenced by every onboarding entity's
 * `principalNodeIDs` in a single, server-filtered query, and returns them keyed
 * by node ID.
 *
 * This replaces the old per-suggestion statement traversal: `principalNodeIDs`
 * is the authoritative principal list, so once we have the nodes by ID,
 * resolution is a direct lookup (see `resolvePrincipalsFromNodeMap`). Skipped
 * when there are no principal IDs.
 */
export function useOnboardingPrincipalNodes({
  householdId,
  suggestions,
}: {
  householdId: string;
  suggestions: SuggestionWithPrincipalNodeIDs[];
}): UseOnboardingPrincipalNodesReturn {
  const nodeIDs = useMemo(
    () =>
      uniq(
        suggestions.flatMap((s) => s.onboardingEntity?.principalNodeIDs ?? [])
      ),
    [suggestions]
  );

  const skip = !householdId || nodeIDs.length === 0;

  const { data, loading } = useFetchOnboardingPrincipalNodesQuery({
    variables: { householdID: householdId, nodeIDs },
    skip,
  });

  const principalNodesById = useMemo(() => {
    const nodes = data?.fetchHouseholdKGV2Nodes ?? [];
    return new Map<string, KGV2NodeForGrouping>(
      nodes.map((node) => [node.id, node])
    );
  }, [data]);

  return {
    principalNodesById,
    // When skipped, Apollo reports loading=false; guard anyway so we never gate
    // the form on a query that never runs.
    loading: skip ? false : loading,
  };
}
